"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Home, Megaphone, Store, User } from "lucide-react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { openAuthModal } from "@/hooks/useAuthModal";
import useFixedBarHeight, { FOOTER_NAV_HEIGHT_VAR } from "@/hooks/useFixedBarHeight";
import {
    ADS_HOME_HREF, ADS_SERVICES_CHANGE_EVENT, ADS_SERVICES_HREF, ADS_SERVICES_TOGGLE_EVENT,
} from "@/helpers/ads";
import { FooterIcons as SharedFooterIcons } from "../FooterNavigationBlog";
import { FOOTER_NAV_BAR_CLASS, footer_nav_item_class } from "../footerNavStyles";

const FooterIcons = {
    home: { fill: SharedFooterIcons.home.fill, outline: <Home size={24} /> },
    services: { fill: <Megaphone size={24} fill="currentColor" />, outline: <Megaphone size={24} /> },
    shop: { fill: <Store size={24} fill="currentColor" />, outline: <Store size={24} /> },
    profile: { fill: SharedFooterIcons.profile.fill, outline: <User size={24} /> },
};

const normalize = (path = "") => path.split("?")[0].replace(/\/+$/, "") || "/";

/**
 * نویگیشن پایین موبایل بخش «ژومنت Ads» (فیگما: خانه، خدمات تبلیغاتی، فروشگاه،
 * حساب کاربری). «خدمات تبلیغاتی» به صفحه‌ی لیست خدمات می‌رود و برای خودِ آن
 * صفحه و زیرصفحه‌های خدمت‌ها فعال است.
 */
const FooterNavigationAds = () => {
    const barRef = useFixedBarHeight(FOOTER_NAV_HEIGHT_VAR);
    const pathname = normalize(usePathname() || "");
    const router = useRouter();
    const { is_auth } = useAuthContext();
    const [services_open, set_services_open] = useState(false);

    // مودال خدمات داخل هدر است؛ وضعیتش با رویداد window به این نوار می‌رسد
    useEffect(() => {
        const on_change = (event) => set_services_open(Boolean(event.detail?.open));
        window.addEventListener(ADS_SERVICES_CHANGE_EVENT, on_change);
        return () => window.removeEventListener(ADS_SERVICES_CHANGE_EVENT, on_change);
    }, []);

    const toggle_services = () => window.dispatchEvent(new CustomEvent(ADS_SERVICES_TOGGLE_EVENT));

    const handleAccountClick = () => {
        if (services_open) toggle_services();
        if (is_auth) router.push("/account/");
        else openAuthModal("/account/");
    };

    const services_base = normalize(ADS_SERVICES_HREF);

    // وقتی مودال خدمات باز است فقط همان آیتم فعال دیده شود
    const items = [
        { key: "home", title: "خانه", href: ADS_HOME_HREF, active: !services_open && pathname === normalize(ADS_HOME_HREF) },
        { key: "services", title: "خدمات تبلیغاتی", onClick: toggle_services, active: services_open || pathname === services_base || pathname.startsWith(`${services_base}/`) },
        { key: "shop", title: "فروشگاه", href: "/", active: false },
        { key: "profile", title: "حساب کاربری", onClick: handleAccountClick, active: !services_open && pathname.startsWith("/account") },
    ];

    return (
        <nav ref={barRef} aria-label="نویگیشن ژومنت Ads" className={FOOTER_NAV_BAR_CLASS}>
            {items.map(({ key, title, href, onClick, active }) => {
                const className = footer_nav_item_class(active);
                const content = (
                    <>
                        {active ? FooterIcons[key].fill : FooterIcons[key].outline}
                        <span>{title}</span>
                    </>
                );

                if (onClick) {
                    return (
                        <button key={key} type="button" onClick={onClick} className={className}>
                            {content}
                        </button>
                    );
                }

                return (
                    <Link key={key} href={href} aria-current={active ? "page" : undefined} onClick={() => services_open && toggle_services()} className={className}>
                        {content}
                    </Link>
                );
            })}
        </nav>
    );
};

export default FooterNavigationAds;
